import { ActionTree }                                                                                  from 'vuex';
import { RootState }                                                                                   from '@/store/store.types';
import { Notification }                                                                                from '@/components/notification/notification';
import { useGlobals }                                                                                  from '@/hooks/use-globals';
import { ChangePasswordPayload, EditUserRequest, GetUsersResponse, PostUserRequest, User, UsersState } from '@/views/users/users.types';
import * as usersService                                                                               from '@/views/users/services/users.service';

const { i18n } = useGlobals();

const actions: ActionTree<UsersState, RootState> = {
  async getUsers({ commit }): Promise<void> {
    try {
      const { data }: { data: GetUsersResponse } = await usersService.getUsers();
      const users = data.content.map((user: User) => ({
        ...user,
        status: user?.status.charAt(0).toUpperCase() + user?.status.substr(1).toLowerCase(),
      }));
      commit('setUsers', users);
    } catch (e) {
      Notification.error(
        i18n.t('vUsers.notification.error.title') as string,
        i18n.t('vUsers.notification.error.getUsers') as string,
      );
      throw new Error(e);
    }
  },
  async addUser({ commit, dispatch }, user: PostUserRequest): Promise<void> {
    try {
      await usersService.saveUser(user);
      commit('toggleModal', false);
      await dispatch('getUsers');
      Notification.success(
        i18n.t('vUsers.notification.success.title') as string,
        i18n.t('vUsers.notification.success.addUser') as string,
      );
    } catch (error) {
      if (error.response.data.length) {
        commit('setServerErrors', error.response.data);
      } else {
        Notification.error('vUsers.notification.error.title', i18n.t('vUsers.notification.error.addUser') as string);
        throw new Error(error);
      }
    }
  },
  async editUser({ dispatch, commit }, user: EditUserRequest): Promise<void> {
    try {
      await usersService.editUser(user);
      commit('toggleModal', false);
      await dispatch('getUsers');
      Notification.success(
        i18n.t('vUsers.notification.success.title') as string,
        i18n.t('vUsers.notification.success.editUser') as string,
      );
    } catch (error) {
      if (error.response.data?.length) {
        commit('setServerErrors', error.response.data);
      } else {
        Notification.error('vUsers.notification.error.title', i18n.t('vUsers.notification.error.editUser') as string);
        throw new Error(error);
      }
    }
  },
  async changePassword({ commit }, payload: ChangePasswordPayload): Promise<void> {
    try {
      const { uuid, password } = payload;
      await usersService.editPassword(uuid, password);
      commit('toggleModal', false);
      Notification.success(
        i18n.t('vUsers.notification.success.title') as string,
        i18n.t('vUsers.notification.success.changePassword') as string,
      );
    } catch (error) {
      Notification.error('vUsers.notification.error.title', i18n.t('vUsers.notification.error.changePassword') as string);
      throw new Error(error);
    }
  },
  async removeUser({ dispatch }, user: User): Promise<void> {
    try {
      await usersService.removeUser(user);
      await dispatch('getUsers');
      Notification.success(
        i18n.t('vUsers.notification.success.title') as string,
        i18n.t('vUsers.notification.success.removeUser') as string,
      );
    } catch (e) {
      throw new Error(e);
    }
  },
};

export default actions;
